import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MarketingNav, Footer } from "@/components/marketing/MarketingNav";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { useAuth } from "@/context/AuthContext";
import { Check, Crown, Sparkles } from "lucide-react";

export default function Pricing() {
  const { data } = usePolling(async () => (await api.get("/plans/catalog")).data, 0, []);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);
  const { user, plan: currentPlan, ent } = useAuth();
  const nav = useNavigate();
  const plans = data?.plans || [];
  const trialUsed = Boolean(ent?.trial_used);

  const startCheckout = async (p) => {
    const lookup = yearly ? p.lookup_yearly : p.lookup_monthly;
    if (!lookup) return;
    if (!user) { nav("/signup", { state: { from: "/pricing" } }); return; }
    setBusy(p.id);
    try {
      const { data: res } = await api.post("/payments/checkout", {
        lookup_key: lookup, origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      setBusy(null);
    }
  };

  return (
    <div className="bg-[var(--bg)] text-[var(--text)] min-h-screen">
      <MarketingNav />
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 text-center">
        <span className="text-xs uppercase tracking-wider text-aurum font-medium">Pricing</span>
        <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold tracking-tight">Trade the plan. Priced simply.</h1>
        <p className="mt-4 text-[var(--text-sec)] max-w-xl mx-auto">Start free with paper trading. Upgrade for live XAU/USD, backtesting, alerts and custom strategy monitoring.</p>

        <div className="mt-8 inline-flex rounded-full border border-[var(--border2)] p-1 text-sm">
          <button onClick={() => setYearly(false)} data-testid="billing-monthly" className={`px-4 py-1.5 rounded-full transition-all ${!yearly ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`}>Monthly</button>
          <button onClick={() => setYearly(true)} data-testid="billing-yearly" className={`px-4 py-1.5 rounded-full transition-all ${yearly ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`}>Yearly <span className="text-[10px] opacity-80">· 2 months free</span></button>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6 text-left">
          {plans.map((p) => {
            const featured = p.id === "PRO";
            const price = yearly ? p.price_yearly : p.price_monthly;
            const isCurrent = user && currentPlan === p.id;
            const Icon = p.id === "ELITE" ? Crown : Sparkles;
            const savings = p.price_monthly > 0 ? p.price_monthly * 12 - p.price_yearly : 0;
            const canTrial = p.trial_days && user && currentPlan === "FREE" && !trialUsed;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`rounded-2xl border p-7 flex flex-col relative ${featured ? "border-aurum bg-[rgba(214,174,87,0.05)]" : "border-[var(--border2)] bg-[var(--card2)]"}`}>
                {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] rounded-full px-3 py-0.5 btn-gold font-semibold">Most popular</span>}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center gap-2">{p.id !== "FREE" && <Icon size={16} className="text-aurum" />}{p.name}</h3>
                  {isCurrent && <span className="text-[10px] rounded-full px-2 py-0.5 border border-[var(--border2)] text-[var(--text-sec)]">Your plan</span>}
                </div>
                <p className="mt-1 text-xs text-[var(--text-muted)]">{p.tagline}</p>
                <div className="mt-4 tnum flex items-end gap-2 flex-wrap">
                  <span className="text-4xl font-extrabold">${price}</span>
                  {price > 0 && <span className="text-sm text-[var(--text-muted)] mb-1"> / {yearly ? "year" : "month"}</span>}
                  {price === 0 && <span className="text-sm text-[var(--text-muted)] mb-1"> / forever</span>}
                  {yearly && savings > 0 && (
                    <span data-testid={`savings-${p.id}`} className="mb-1.5 text-[11px] rounded-full px-2 py-0.5 font-semibold text-bull" style={{ background: "rgba(33,197,142,0.12)", border: "1px solid rgba(33,197,142,0.28)" }}>Save ${savings}/yr</span>
                  )}
                </div>
                {canTrial && <div className="mt-2 text-[11px] text-aurum font-medium">✨ Starts with a 7-day free trial</div>}
                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[var(--text-sec)]">
                      <Check size={15} className="text-bull mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {p.id === "FREE" ? (
                  <button onClick={() => nav(user ? "/app" : "/signup")} data-testid={`select-${p.id}`}
                    className="mt-7 rounded-full py-2.5 text-center text-sm font-semibold transition-all border border-[var(--border2)] hover:border-aurum hover:text-aurum">
                    {isCurrent ? "Current plan" : "Start free"}
                  </button>
                ) : (
                  <button onClick={() => startCheckout(p)} disabled={busy === p.id || isCurrent} data-testid={`select-${p.id}`}
                    className={`mt-7 rounded-full py-2.5 text-center text-sm font-semibold transition-all disabled:opacity-50 ${featured ? "btn-gold hover:brightness-110" : "border border-[var(--border2)] hover:border-aurum hover:text-aurum"}`}>
                    {isCurrent ? "Current plan" : busy === p.id ? "Redirecting…" : canTrial ? "Start 7-day free trial" : `Upgrade to ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-10 text-xs text-[var(--text-muted)]">Cancel anytime. Trading involves substantial risk. Aurum provides analysis, not financial advice, and does not guarantee results.</p>
      </section>
      <Footer />
    </div>
  );
}
